import { useMemo, useState } from 'react';
import { MODELS, QUANT_LABEL, QUANT_QUALITY_PENALTY, findModel } from '../catalog.js';
import { CLOUD_REFERENCES, cloudQualityForTask, estimateCloudCost, findCloud } from '../cloud.js';
import { estimateFit, recommendQuantForMachine, recommendRuntimeForMachine } from '../fit.js';
import type { MachineProfile } from '../types.js';
import { Bar, TierBadge, VerdictPill } from '../ui.js';

interface CompareTabProps {
  machine: MachineProfile;
  mode: 'beginner' | 'pro';
}

const DEFAULT_CANDIDATES = ['qwen-2.5-coder-14b', 'deepseek-coder-v2-16b', 'codestral-22b'];

export function CompareTab({ machine, mode }: CompareTabProps) {
  const [candidates, setCandidates] = useState<string[]>(DEFAULT_CANDIDATES);
  const [showCloud, setShowCloud] = useState(true);
  const [cloudId, setCloudId] = useState<string>('claude-sonnet-4-6');
  const cloud = findCloud(cloudId) ?? CLOUD_REFERENCES[0];
  const runtime = recommendRuntimeForMachine(machine);

  const rows = useMemo(
    () =>
      candidates
        .map((id) => findModel(id))
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .map((m) => {
          const quant = recommendQuantForMachine(machine, m);
          const fit = estimateFit(machine, m, quant, runtime);
          const adjustedQuality = Math.max(0, m.baseQualityScore - QUANT_QUALITY_PENALTY[quant]);
          return { model: m, fit, adjustedQuality };
        }),
    [candidates, machine, runtime],
  );

  const winner =
    rows.length > 0 ? rows.reduce((acc, r) => (compositeScore(r) > compositeScore(acc) ? r : acc)) : null;
  const cloudCost = estimateCloudCost(cloud, 1500 * 30 * 60, 500 * 30 * 60);

  function setCandidate(idx: number, id: string) {
    setCandidates((prev) => prev.map((c, i) => (i === idx ? id : c)));
  }

  function addCandidate() {
    const next = MODELS.find((m) => !candidates.includes(m.id));
    if (next) setCandidates((prev) => [...prev, next.id]);
  }

  function removeCandidate(idx: number) {
    setCandidates((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <div className="lab-compare">
      <header className="lab-compare-head">
        <h3>Head-to-head on {machine.nickname}</h3>
        <p>
          Compare any number of local models, with optional cloud reference. Same machine, same runtime,
          fit-engine quants. {mode === 'pro' && 'Pro mode adds bars and per-row composite scores.'}
        </p>
        <div className="lab-compare-actions">
          <label className="lab-compare-cloud-toggle">
            <input type="checkbox" checked={showCloud} onChange={(e) => setShowCloud(e.target.checked)} />
            <span>Show cloud reference</span>
          </label>
          {showCloud && (
            <label>
              <span>Cloud</span>
              <select className="lab-select" value={cloudId} onChange={(e) => setCloudId(e.target.value)}>
                {CLOUD_REFERENCES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            className="ghost-button"
            onClick={addCandidate}
            disabled={candidates.length >= MODELS.length}
          >
            + Add candidate
          </button>
        </div>
      </header>

      <div className="lab-compare-table-wrap">
        <table className="lab-table lab-compare-table">
          <thead>
            <tr>
              <th>Model</th>
              <th>Verdict</th>
              <th>Quant</th>
              <th>tok/s</th>
              <th>First tok</th>
              <th>Quality (post-quant)</th>
              <th>Coding tier</th>
              <th>Memory</th>
              <th>Headroom</th>
              {mode === 'pro' && <th>Composite</th>}
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.model.id} className={winner?.model.id === r.model.id ? 'lab-row-winner' : ''}>
                <td>
                  <select
                    className="lab-select lab-select-inline"
                    aria-label={`Candidate ${idx + 1}: ${r.model.displayName}`}
                    value={r.model.id}
                    onChange={(e) => setCandidate(idx, e.target.value)}
                  >
                    {MODELS.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.displayName}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <VerdictPill verdict={r.fit.verdict} />
                </td>
                <td>{QUANT_LABEL[r.fit.quantization]}</td>
                <td>{r.fit.expectedTps}</td>
                <td>{r.fit.expectedTtftMs}ms</td>
                <td>{r.adjustedQuality.toFixed(1)}</td>
                <td>
                  <TierBadge tier={r.fit.codingUsefulnessTier} />
                </td>
                <td>{r.fit.requiredGb} GB</td>
                <td>{r.fit.headroomRatio}×</td>
                {mode === 'pro' && <td>{compositeScore(r).toFixed(1)}</td>}
                <td>
                  {candidates.length > 1 && (
                    <button
                      type="button"
                      className="lab-row-remove"
                      onClick={() => removeCandidate(idx)}
                      aria-label="remove"
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {showCloud && (
              <tr className="lab-row-cloud">
                <td>
                  <strong>{cloud.displayName}</strong> <span className="lab-cloud-tag">cloud</span>
                </td>
                <td>
                  <span className="verdict-pill verdict-great">cloud</span>
                </td>
                <td>—</td>
                <td>{cloud.approxTps}</td>
                <td>{cloud.approxLatencyMs}ms</td>
                <td>{cloudQualityForTask(cloud, 'coding').toFixed(1)}</td>
                <td>
                  <TierBadge tier={cloud.codingTier} />
                </td>
                <td>—</td>
                <td>—</td>
                {mode === 'pro' && <td>{(cloud.baseQualityScore + cloud.approxTps * 0.3).toFixed(1)}</td>}
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {winner && (
        <section className="lab-compare-summary">
          <h4>Winner: {winner.model.displayName}</h4>
          <p>
            Best composite of speed, quality, and headroom on {machine.nickname}.{' '}
            {explainWinner(winner.model.displayName, winner)}
          </p>
        </section>
      )}

      {showCloud && (
        <section className="lab-compare-cloud-cost">
          <h4>Cloud cost reference</h4>
          <p>
            At ~60 tasks/hour for 30 hours/month with {cloud.displayName}, expect{' '}
            <strong>${cloudCost.toFixed(0)}/month</strong> in API spend. The local picks above cost $0 after
            the GPU/Mac is paid for.
          </p>
        </section>
      )}

      {mode === 'pro' && rows.length > 0 && (
        <div className="lab-compare-bars">
          {rows.map((r) => (
            <div key={r.model.id} className="lab-compare-bar-row">
              <span className="lab-compare-bar-label">{r.model.displayName}</span>
              <Bar
                value={r.fit.expectedTps}
                max={Math.max(60, ...rows.map((x) => x.fit.expectedTps))}
                tone="cyan"
              />
              <strong>{r.fit.expectedTps} tok/s</strong>
              <Bar value={r.adjustedQuality} max={100} tone="mint" />
              <strong>Q {r.adjustedQuality.toFixed(0)}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function compositeScore(row: { fit: ReturnType<typeof estimateFit>; adjustedQuality: number }): number {
  const verdictWeight: Record<string, number> = {
    great: 1.0,
    good: 0.85,
    usable: 0.6,
    'chat-only': 0.35,
    technical: 0.15,
    no: 0,
  };
  const w = verdictWeight[row.fit.verdict] ?? 0.5;
  return (
    (row.adjustedQuality * 0.6 +
      Math.min(row.fit.expectedTps, 50) * 0.7 +
      Math.min(row.fit.headroomRatio, 3) * 5) *
    w
  );
}

function explainWinner(
  name: string,
  winner: { fit: ReturnType<typeof estimateFit>; adjustedQuality: number },
): string {
  return `${winner.fit.expectedTps} tok/s, quality ${winner.adjustedQuality.toFixed(0)}, ${winner.fit.headroomRatio}× headroom — ${name} balances all three best.`;
}
